import { useMemo } from "react";
import { useHandoff } from "@ncp/api-client";

/**
 * Cross-application links. The Platform Console never renders case work itself: anything that points at a case, the AI Inbox or a
 * proposal opens in the Case Management back office, and applicant-facing previews open in the portal.
 *
 * Hosts come from `VITE_BACKOFFICE_URL` / `VITE_PORTAL_URL` when set (the Ministry's internal hosts set them at build: the back office at
 * the root of ncp.moet.gov.ae, the portal under /portal); otherwise they are derived from the console host (`admin.<domain>` →
 * `backoffice.<domain>` / `<domain>`) with the Vite dev ports as a last resort.
 */
type Surface = "backoffice" | "portal" | "assistant";

const DEV_PORTS: Record<Surface, number> = { portal: 5173, backoffice: 5174, assistant: 5176 };

function derive(sub: Surface): string {
  if (typeof window === "undefined") return "";
  const { protocol, hostname, port } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") return `${protocol}//${hostname}:${DEV_PORTS[sub]}`;
  const rest = hostname.replace(/^admin\./, "");
  const host = sub === "backoffice" ? `backoffice.${rest}` : sub === "assistant" ? `ai.${rest}` : rest;
  return `${protocol}//${host}${port ? `:${port}` : ""}`;
}

export const BACKOFFICE_URL: string = import.meta.env.VITE_BACKOFFICE_URL || derive("backoffice");
export const PORTAL_URL: string = import.meta.env.VITE_PORTAL_URL || derive("portal");
export const ASSISTANT_URL: string = import.meta.env.VITE_ASSISTANT_URL || derive("assistant");

export function backofficeHref(path: string): string {
  return `${BACKOFFICE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export function portalHref(path: string): string {
  return `${PORTAL_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export function assistantHref(path = "/"): string {
  return `${ASSISTANT_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Openers for the sibling surfaces, in a new tab so the operator keeps their place in the console. Staff surfaces receive the session
 * through a one-time handoff code; the portal is applicant-facing and always opens signed out.
 */
export function useSurfaceLinks() {
  const { open } = useHandoff();
  return useMemo(
    () => ({
      openInBackoffice: (path: string) => open(backofficeHref(path)),
      openInAssistant: (path = "/") => open(assistantHref(path)),
      openInPortal: (path: string) => void window.open(portalHref(path), "_blank", "noopener"),
    }),
    [open],
  );
}
